import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { deleteFilter } from 'api/Filter';

import { ia } from 'lib/helpers/utility';
import useModal from 'lib/hooks/useModal';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Confirm from 'components/shared/Modal/Confirm/Confirm';
import ToggleSwitch from 'components/shared/ToggleSwitch/ToggleSwitch';
import SaveFilterModal from 'components/shared/Filters/components/SaveFilterModal';

import { useViewTabs } from '../hooks/useViewTabs';
import { useAutoTabSwitch } from '../hooks/useAutoTabSwitch';

const ViewTabs = ({
  items = [],
  category,
  filters,
  title,
  updateFilters,
  resetFilters,
  removeDefaults = false
}) => {
  const navigate = useNavigate();
  const [_, setSearchParams] = useSearchParams();
  const { isOpen, closeModal, openModal } = useModal();
  const { isOpen: isEditOpen, closeModal: closeEditModal, openModal: openEditModal } = useModal();

  const viewToDeleteRef = useRef(null);
  const viewToEditRef = useRef(null);

  useEffect(() => {
    if (isOpen) viewToDeleteRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (isEditOpen) viewToEditRef.current = isEditOpen;
  }, [isEditOpen]);

  const viewToDelete = isOpen || viewToDeleteRef.current;
  const viewToEdit = isEditOpen || viewToEditRef.current;

  const { view, activeItem, filtersData, toggleItems } = useViewTabs({
    title,
    items,
    category,
    updateFilters,
    resetFilters,
    removeDefaults,
    openModal,
    openEditModal
  });

  useAutoTabSwitch({ items: toggleItems, updateFilters });

  useEffect(() => {
    if (filters?.status && view) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        const currentView = newParams.get('view');
        const viewMatchesItem = items.some((_item) => _item.value === currentView);
        if (viewMatchesItem) {
          newParams.delete('view');
        }
        return newParams;
      });
    }
  }, [filters]);

  const { mutate: deleteFilterHandler, isLoading: isDeletingFilter } = useCustomMutation({
    mutationFn: (params) => deleteFilter(navigate, params),
    successMessage: 'Filter deleted',
    errorMessage: "Couldn't delete filter. Please try again.",
    invalidateQueryKey: ['default-filters', ['filters', category]],
    onSuccess: () => {
      resetFilters();
      setSearchParams((prev) => {
        const currentView = prev.get('view');
        if (currentView === viewToDelete?.name) {
          prev.delete('view');
        }
        return prev;
      });
      closeModal();
    }
  });
  const handleDeleteFilter = () => deleteFilterHandler({ id: viewToDelete?.id });

  return (
    <>
      {ia(toggleItems, 1) && (
        <div className="flex w-full min-w-0 gap-2">
          <ToggleSwitch items={toggleItems} active={activeItem?.value || view || null} />
        </div>
      )}

      <Confirm
        isOpen={!!isOpen}
        variant="danger"
        handleClose={closeModal}
        title={`Delete view "${viewToDelete?.name || ''}"`}
        icon="trash-mono"
        message="You cannot do undo this action"
        footerActions={{
          primary: {
            text: 'Delete',
            onClick: handleDeleteFilter,
            color: 'danger',
            isLoading: isDeletingFilter
          }
        }}
      />
      <SaveFilterModal
        title="Rename view"
        isOpen={!!isEditOpen}
        currentView={viewToEdit}
        onCancel={closeEditModal}
        filters={filters}
        category={category}
        currentViews={filtersData?.filters}
        items={items}
      />
    </>
  );
};

export default ViewTabs;
